import * as React from 'react';
import { ChangeEvent, ChangeEventHandler, FocusEventHandler, FocusEvent, useState, useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import { TransProps, useTranslation } from 'react-i18next';
import { NumberFormatValues, NumericFormat, PatternFormat } from 'react-number-format';

import { IconName, InputTheme, Language, ValidationBarTheme } from '../../enums';
import { InputLabelBaseComponent, InputLabelSpan } from '../../theme';
import { UseState } from '../../types';
import { Icon } from '../icon/icon.component';

import { 
  BaseInput, 
  Container, 
  IconContainer, 
  InputContainer, 
  StyledValidationBar, 
  TogglePasswordType 
} from './input.styled';
import { InputProps } from './input.types';

import 'flatpickr/dist/l10n/pl.js';

export const Input: React.FC<InputProps> = (props: InputProps) => {
  const { t }: TransProps<never> = useTranslation();
  const {
    value = '',
    label,
    disabled = false,
    type = 'text',
    onChange,
    onBlur,
    onKeyPress,
    className,
    validationMessage,
    description,
    icon,
    inputTheme = InputTheme.Standard,
    labelSecondBackground,
    mask,
    format,
    withoutMinDate = false,
    withOnlyNumbersAllowed = false,
    withPriceParsing = false,
    onIconClickAction
  }: InputProps = props;
  const [passwordVisible, setPasswordVisible]: UseState<boolean> = useState<boolean>(false);
  const name: string | undefined = label?.replace(/\ /g, '').toLowerCase();
  const [isLabelOnTop, setIsLabelOnTop]: UseState<boolean> = useState<boolean>(!!value);
  const [inputValue, setInputValue]: UseState<string | undefined> = useState<string | undefined>(value);

  useEffect(
    (): void => {
      setIsLabelOnTop(!!value && !!label);
      setInputValue(value);
    },
    [value]
  );

  const onChangeAction: ChangeEventHandler<HTMLInputElement> = (e: ChangeEvent<HTMLInputElement>): void => {
    const valueFromEvent: string = withPriceParsing ? e.target.value.replace(/\s/g, '') : e.target.value;
    if (onChange) {
      onChange(valueFromEvent);
    }
    setIsLabelOnTop(!!valueFromEvent);
    setInputValue(valueFromEvent);
  };

  const onChangeDateAction: (dates: Date[]) => void = (dates: Date[]): void => {
    if (dates.length) {
      const timestamp: number = new Date(dates[0]).getTime();
      setIsLabelOnTop(true);
      if (onChange) {
        onChange(timestamp.toString());
      }
      setInputValue(timestamp.toString());
    }
  };

  const onBlurAction: FocusEventHandler<HTMLInputElement> = (e: FocusEvent<HTMLInputElement, Element>): void => {
    const valueFromEvent: string = withPriceParsing ? e.target.value.replace(/\s/g, '') : e.target.value.trim();
    if (onBlur) {
      onBlur(valueFromEvent);
    }
    setIsLabelOnTop(!!valueFromEvent);
    setInputValue(valueFromEvent);
  };

  const onValueChangeAction: (e: NumberFormatValues) => void = (e: NumberFormatValues): void => {
    const valueFromEvent: string = e.formattedValue;
    if (onChange) {
      onChange(valueFromEvent);
    }
    setIsLabelOnTop(!!valueFromEvent);
    setInputValue(valueFromEvent);
  };

  const onFocusAction: FocusEventHandler<HTMLInputElement> = (): void => setIsLabelOnTop(true);

  const togglePasswordVisibleState: () => void = (): void => setPasswordVisible(!passwordVisible);

  return (
    <Container className={className}>
      {type !== 'date' ? (
        <InputContainer>
          {!!label && (
            <InputLabelBaseComponent
              htmlFor={name}
              $isOnTop={isLabelOnTop}
              $disabled={disabled}
              $withIcon={!!icon}
              $inputTheme={inputTheme}
              $topPartBackground={labelSecondBackground}
            >
              <InputLabelSpan>{label}</InputLabelSpan>
            </InputLabelBaseComponent>
          )}
          {format ? (
            <PatternFormat 
              id={name}
              name={name}
              disabled={disabled}
              onValueChange={onValueChangeAction}
              onBlur={onBlurAction}
              onFocus={onFocusAction}
              onKeyPress={onKeyPress}
              type={type}
              data-testid='input-pattern'
              value={value}
              $withIcon={!!icon}
              $inputTheme={inputTheme}
              allowEmptyFormatting
              format={format}
              mask={mask}
              customInput={BaseInput}
            />
          ) : withOnlyNumbersAllowed || withPriceParsing ? (
            <NumericFormat 
              id={name}
              name={name}
              disabled={disabled}
              value={inputValue}
              onChange={onChangeAction}
              onBlur={onBlurAction}
              onFocus={onFocusAction}
              onKeyPress={onKeyPress}
              type={type}
              data-testid='input-numeric'
              $withIcon={!!icon}
              $inputTheme={inputTheme}
              valueIsNumericString
              thousandSeparator={withPriceParsing ? ' ' : ''}
              decimalScale={withPriceParsing ? 2 : undefined}
              customInput={BaseInput}
            />
          ) : (
            <BaseInput
              id={name}
              name={name}
              disabled={disabled}
              value={inputValue}
              onChange={onChangeAction}
              onBlur={onBlurAction}
              onFocus={onFocusAction}
              onKeyPress={onKeyPress}
              type={passwordVisible ? 'text' : type}
              data-testid='input'
              $withRightButton={type === 'password' && !disabled}
              $withIcon={!!icon}
              $inputTheme={inputTheme}
            />
          )}
          {(type === 'password' && !disabled && !!inputValue) && (
            <TogglePasswordType onClick={togglePasswordVisibleState}>
              {passwordVisible ? t('onarte.ui.input.hide') : t('onarte.ui.input.show')}
            </TogglePasswordType>
          )}
          {!!icon && (
            <IconContainer $withOnClick={!!onIconClickAction} onClick={onIconClickAction}>
              <Icon name={icon} size={20} />
            </IconContainer>
          )}
        </InputContainer>
      ) : (
        <Flatpickr
          value={value ? parseInt(value, 10) : undefined}
          onChange={onChangeDateAction}
          options={{
            minDate: (!withoutMinDate && !disabled) ? 'today' : undefined,
            time_24hr: true,
            enableTime: true,
            enableSeconds: true,
            minuteIncrement: 1,
            altFormat: 'd M Y, H:i:S',
            altInput: true,
            locale: Language.Pl,
            wrap: true,
          }}
        >
          <InputContainer>
            {!!label && (
              <InputLabelBaseComponent
                htmlFor={name}
                $isOnTop={isLabelOnTop}
                $disabled={disabled}
                $withIcon={!!icon}
                $inputTheme={inputTheme}
              >
                {label}
              </InputLabelBaseComponent>
            )}
            <BaseInput
              id={name}
              name={name}
              disabled={disabled}
              type='text'
              data-testid='input'
              $withRightButton={false}
              $inputTheme={inputTheme}
              $withIcon
              data-input
            />
            <IconContainer>
              <Icon name={IconName.CalendarDateRange} size={20} />
            </IconContainer>
          </InputContainer>
        </Flatpickr>
      )}
      <StyledValidationBar message={description} barTheme={ValidationBarTheme.Information} />
      <StyledValidationBar message={validationMessage} />
    </Container>
  );
};
